import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from 'styled-components'

import { Alert, CountryFlag, Documents, Drawer, helpers, LineItem, SequenceNumber, Text } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import CustomsChip from '@/parcels/components/CustomsChip/CustomsChip'
import InternationalChip from '@/parcels/components/InternationalChip/InternationalChip'
import Notes from '@/parcels/components/Notes/Notes'
import ParcelCarrier from '@/parcels/components/ParcelCarrier/ParcelCarrier'
import ParcelDate from '@/parcels/components/ParcelDate/ParcelDate'
import ParcelDestination from '@/parcels/components/ParcelDestination/ParcelDestination'
import ParcelEta from '@/parcels/components/ParcelEta/ParcelEta'
import ParcelMethod from '@/parcels/components/ParcelMethod/ParcelMethod'
import ParcelOrigin from '@/parcels/components/ParcelOrigin/ParcelOrigin'
import ParcelPickupDate from '@/parcels/components/ParcelPickupDate/ParcelPickupDate'
import ParcelTrackingNumber from '@/parcels/components/ParcelTrackingNumber/ParcelTrackingNumber'
import ParcelTypeChip from '@/parcels/components/ParcelTypeChip/ParcelTypeChip'
import ParcelUserInfo from '@/parcels/components/ParcelUserInfo/ParcelUserInfo'
import PrepareParcelButton from '@/parcels/components/PrepareParcelButton/PrepareParcelButton'
import ShippingCost from '@/parcels/components/ShippingCost/ShippingCost'
import ShippingCostInbound from '@/parcels/components/ShippingCostInbound/ShippingCostInbound'
import {
  getParcelAddressText,
  getParcelAlertLabel,
  getParcelCustomerContactAddress,
  getParcelItemsQuantity,
  getParcelSenderName,
  getParcelSequenceInfo,
  isInboundParcel,
  isInternationalAddresses,
} from '@/parcels/helpers/helpers'

import { parcelStatusVariants } from '../ParcelStatus/constants'
import ParcelStatus from '../ParcelStatus/ParcelStatus'
import ParcelDetailDrawerSkeleton from './ParcelDetailDrawerSkeleton/ParcelDetailDrawerSkeleton'
import * as Styles from './styles'

const ParcelDetailDrawer = ({
  show,
  onClose,
  onViewParcelDetail,
  loading,
  onPrevParcel,
  onNextParcel,
  parcelDetail,
  shippingSkipPacking,
  onPrepareParcel,
  onAutoPrepareParcel,
  preparingParcel,
  allowPrepareParcel,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { info } = useContext(AppContext)

  const related = parcelDetail?.related
  const senderAddress = parcelDetail?.origin
  const senderName = getParcelSenderName(parcelDetail)
  const destinationAddress = parcelDetail?.destination
  const isInternational = isInternationalAddresses(senderAddress, destinationAddress)
  const isInbound = isInboundParcel(parcelDetail)
  const sequenceInfo = getParcelSequenceInfo(parcelDetail)
  const alertLabel = getParcelAlertLabel(parcelDetail)
  const hasCosts = related?.total != null || related?.base != null
  const documents = parcelDetail?.documents
  const hasDocuments = Boolean(documents?.labels.length || documents?.invoice_url)

  return (
    <Drawer
      show={show}
      onClose={onClose}
      onExpand={onViewParcelDetail}
      onPrevious={onPrevParcel}
      onNext={onNextParcel}
      options={{ showOnNext: true, showOnPrevious: true }}
    >
      {loading ? (
        <ParcelDetailDrawerSkeleton />
      ) : (
        <Styles.Container>
          {alertLabel && <Alert type="warning">{t(alertLabel)}</Alert>}
          <Styles.HeaderContainer>
            <Styles.HeaderTitleContainer>
              <Styles.HeaderInfoContainer>
                <Text type="titleM" color={theme.colors.outline.default}>
                  {parcelDetail?.order?.name}
                </Text>
                {sequenceInfo && <SequenceNumber position={sequenceInfo.position} total={sequenceInfo.total} />}
                <ParcelTypeChip type={parcelDetail?.type} isExchange={parcelDetail?.order?.is_exchange} />
                {isInternational && <InternationalChip />}
                {related?.with_customs && <CustomsChip />}
                <CountryFlag code={senderAddress?.country?.code} />
                {isInternational && <CountryFlag code={destinationAddress?.country?.code} />}
              </Styles.HeaderInfoContainer>
              {!isInbound && (
                <PrepareParcelButton
                  shippingSkipPacking={shippingSkipPacking}
                  onPrepareParcel={onPrepareParcel}
                  onAutoPrepareParcel={onAutoPrepareParcel}
                  preparingParcel={preparingParcel}
                  allowPrepareParcel={allowPrepareParcel}
                />
              )}
            </Styles.HeaderTitleContainer>
            <Styles.HeaderStatusContainer>
              <ParcelStatus status={parcelDetail?.status} variant={parcelStatusVariants.previewDetail} />
              <ParcelTrackingNumber
                trackingNumber={parcelDetail?.tracking_number}
                trackingUrl={parcelDetail?.order?.order_tracking_page_url}
              />
              <ParcelEta date={related?.eta} />
            </Styles.HeaderStatusContainer>
          </Styles.HeaderContainer>
          <Styles.CustomDivider />
          <Text type="titleM">{t('Detalles del envío')}</Text>
          <Styles.ShippingDetailsContainer>
            <ParcelOrigin addressText={getParcelAddressText(senderAddress, senderName)} />
            <ParcelDestination addressText={getParcelAddressText(destinationAddress, destinationAddress?.locality)} />
            <ParcelDate date={parcelDetail?.created_at} modified={parcelDetail?.modified_at} />
            <ParcelUserInfo
              customer={parcelDetail?.customer_profile}
              recipientAddress={getParcelCustomerContactAddress(parcelDetail)}
              email={parcelDetail?.order?.email}
            />
            <ParcelCarrier
              carrierLogo={parcelDetail?.carrier?.logo_url}
              carrierName={parcelDetail?.carrier?.name}
              carrierServiceName={parcelDetail?.method?.carrier?.service_name}
            />
            <ParcelMethod shippingMethod={parcelDetail?.method} itemsQuantity={getParcelItemsQuantity(parcelDetail)} />
            <ParcelPickupDate date={parcelDetail?.pickup_scheduled} />
          </Styles.ShippingDetailsContainer>
          {parcelDetail?.items?.length > 0 && (
            <>
              <Styles.CustomDivider />
              <Text type="titleM">{t('Productos')}</Text>
              <Styles.ProductsContainer>
                {helpers.flattenLineItems(parcelDetail.items).map(item => (
                  <LineItem key={item.id} item={item} />
                ))}
              </Styles.ProductsContainer>
            </>
          )}
          <Notes notes={related?.notes} />
          {hasCosts && (
            <>
              <Styles.CustomDivider />
              <Text type="titleM">{t('Coste del envío')}</Text>
              {isInbound ? (
                <ShippingCostInbound
                  currency={related?.currency}
                  total={related?.total}
                  base={related?.base}
                  additionalProducts={related?.additional_products}
                />
              ) : (
                <ShippingCost
                  currency={related?.currency}
                  total={related?.total}
                  base={related?.base}
                  duties={related?.duties}
                  tax={related?.tax}
                  currencyAdjustment={related?.currency_adjustment}
                  handlingFees={related?.handling_fees}
                />
              )}
            </>
          )}
          {hasDocuments && (
            <>
              <Styles.CustomDivider />
              <Text type="titleM">{t('Documentos')}</Text>
              <Documents
                transactionDetail={{
                  return_labels: documents.labels,
                  invoice_url: documents.invoice_url,
                }}
                showCustomsDeclaration
                timeZone={info?.selectedStore?.timezone}
              />
            </>
          )}
        </Styles.Container>
      )}
    </Drawer>
  )
}

export default ParcelDetailDrawer
